!(function () {
	'use strict';

	if (customElements.get('ticker-bar')) return;

	const selectors = {
		scale: '[data-ticker-scale]',
		text: '[data-ticker-text]',
		clone: '[data-clone]',
		prev: '[data-ticker-prev]',
		next: '[data-ticker-next]',
	};

	const MANUAL_STEP_RATIO = 0.5; 
	const MANUAL_RECENTER_DELAY = 150; 

	if (!document.getElementById('ticker-manual-style')) {
		const style = document.createElement('style');
		style.id = 'ticker-manual-style';
		style.textContent =
			'[data-manual-scroll]{scrollbar-width:none;-ms-overflow-style:none;}' +
			'[data-manual-scroll]::-webkit-scrollbar{display:none;width:0;height:0;}';
		document.head.appendChild(style);
	}

	customElements.define(
		'ticker-bar',
		class extends HTMLElement {
			settings = { speed: 1.63, space: 100 };

			constructor() {
				super();

				this.autoplay = this.hasAttribute('autoplay');
				this.disableAutoplay = this.hasAttribute('disable-autoplay');
				this.scale = this.querySelector(selectors.scale);
				this.text = this.querySelector(selectors.text);

				if (this.disableAutoplay) {
					// Manual mode: a normal native horizontal scroller (drag with
					// mouse, swipe with touch — exactly like a slider), always
					// scrollable and always looping, no matter how the amount of
					// content compares to the screen width. Every style it needs
					// is set inline in setupManualLoop(), so it doesn't depend on
					// any existing theme CSS.
					this.prevButton = this.querySelector(selectors.prev);
					this.nextButton = this.querySelector(selectors.next);
					this.scale.classList.remove('ticker--unloaded');
					this.onPrevEvent = () => this.onManualNav(-1);
					this.onNextEvent = () => this.onManualNav(1);
					this.resizeEvent = () => this.setupManualLoop();
					return;
				}

				this.speed = this.hasAttribute('speed') ? this.getAttribute('speed') : this.settings.speed;

				this.comparitor = this.text.cloneNode(true);
				this.comparitor.classList.add('ticker__comparitor');
				this.appendChild(this.comparitor);

				this.scale.classList.remove('ticker--unloaded');

				this.checkWidthEvent = this.checkWidth.bind(this);
			}

			connectedCallback() {
				if (this.disableAutoplay) {
					this.setupManualLoop();
					this.prevButton?.addEventListener('click', this.onPrevEvent);
					this.nextButton?.addEventListener('click', this.onNextEvent);
					this.addEventListener('theme:ticker:refresh', this.resizeEvent);
					document.addEventListener('theme:resize:width', this.resizeEvent);
					return;
				}

				this.slider = this.closest('.flickity-slider');
				if (!this.slider) this.checkWidth();

				this.addEventListener('theme:ticker:refresh', this.checkWidthEvent);
				document.addEventListener('theme:resize:width', this.checkWidthEvent);
			}

			disconnectedCallback() {
				if (this.disableAutoplay) {
					this.prevButton?.removeEventListener('click', this.onPrevEvent);
					this.nextButton?.removeEventListener('click', this.onNextEvent);
					document.removeEventListener('theme:resize:width', this.resizeEvent);
					clearTimeout(this.recenterTimeout);
					clearTimeout(this.scrollSettleTimeout);
					return;
				}

				document.removeEventListener('theme:resize:width', this.checkWidthEvent);
			}

			checkWidth() {
				this.text = this.querySelector(selectors.text);

				const paddingLeft = window.getComputedStyle(this).paddingLeft;
				const paddingTotal = paddingLeft ? Number(paddingLeft.replace('px', '')) * 2 : 0;
				const containerWidth = this.clientWidth;
				const comparitorWidth = this.comparitor.clientWidth;
				const textWidth = this.text.clientWidth;
				const windowWidth = window.innerWidth;
				const overflows = containerWidth - paddingTotal < comparitorWidth;
				const isAutoplay = this.autoplay;
				const clonesNeeded = isAutoplay ? parseInt((windowWidth - paddingTotal) / textWidth, 10) : 2;

				if (overflows || isAutoplay) {
					this.scale.querySelectorAll(selectors.text).forEach((el) => el.classList.remove('ticker--animated'));

					const existingClones = Array.from(this.scale.querySelectorAll(selectors.clone));
					if (existingClones.length > 0) existingClones.forEach((el) => el.remove());

					if (isAutoplay || overflows) {
						for (let i = 0; i <= clonesNeeded; i++) {
							const clone = this.text.cloneNode(true);
							clone.setAttribute('data-clone', '');
							this.scale.appendChild(clone);
						}
					}

					const animationTime = ((textWidth / this.settings.space) * Number(this.speed)).toFixed(2);
					this.scale.style.setProperty('--animation-time', `${animationTime}s`);
					this.scale.style.setProperty('--animation-speed', this.speed);

					this.scale.offsetWidth;

					this.scale.querySelectorAll(selectors.text)?.forEach((el) => el.classList.add('ticker--animated'));
				} else {
					this.text.classList.add('ticker--animated');

					const existingClones = Array.from(this.scale.querySelectorAll(selectors.clone));
					if (existingClones.length > 0) existingClones.forEach((el) => el.parentNode.removeChild(el));

					this.text.classList.remove('ticker--animated');
				}
			}

			setupManualLoop() {
				this.scale.setAttribute('data-manual-scroll', '');
				Object.assign(this.scale.style, {
					display: 'flex',
					overflowX: 'auto',
					overflowY: 'hidden',
					WebkitOverflowScrolling: 'touch',
					cursor: 'grab',
					touchAction: 'pan-x',
				});

				Object.assign(this.text.style, {
					display: 'inline-flex',
					flexWrap: 'nowrap',
					flex: '0 0 auto',
				});

				Array.from(this.scale.querySelectorAll(selectors.clone)).forEach((el) => el.remove());

				this.copyWidth = this.text.clientWidth || 1;
				const viewport = this.scale.clientWidth || 1;

				const copiesPerSide = Math.max(2, Math.ceil(viewport / this.copyWidth) + 1);
				this.copiesPerSide = copiesPerSide;

				const makeClone = () => {
					const clone = this.text.cloneNode(true);
					clone.setAttribute('data-clone', '');
					clone.setAttribute('aria-hidden', 'true');
					Object.assign(clone.style, { display: 'inline-flex', flexWrap: 'nowrap', flex: '0 0 auto' });
					clone.querySelectorAll('a, button, input, [tabindex]').forEach((el) => el.setAttribute('tabindex', '-1'));
					return clone;
				};

				for (let i = 0; i < copiesPerSide; i++) {
					this.scale.insertBefore(makeClone(), this.text);
				}
				for (let i = 0; i < copiesPerSide; i++) {
					this.scale.appendChild(makeClone());
				}

				this.prevButton?.removeAttribute('disabled');
				this.nextButton?.removeAttribute('disabled');

				this.scale.scrollLeft = copiesPerSide * this.copyWidth;

				this.setupDrag();
			}

			setupDrag() {
				if (this.dragBound) return;
				this.dragBound = true;

				let dragging = false;
				let startX = 0;
				let startScroll = 0;

				this.scale.addEventListener('pointerdown', (event) => {
					if (event.pointerType !== 'mouse') return; 
					startX = event.clientX;
					startScroll = this.scale.scrollLeft;
					this.scale.style.cursor = 'grabbing';
					this.scale.setPointerCapture(event.pointerId);
					clearTimeout(this.recenterTimeout);
				});

				this.scale.addEventListener('pointermove', (event) => {
					if (!dragging) return;
					this.scale.scrollLeft = startScroll - (event.clientX - startX);
				});

				const endDrag = () => {
					if (!dragging) return;
					dragging = false;
					this.scale.style.cursor = 'grab';
					this.scheduleRecenter();
				};

				this.scale.addEventListener('pointerup', endDrag);
				this.scale.addEventListener('pointercancel', endDrag);
				this.scale.addEventListener('scrollend', () => this.scheduleRecenter());
				this.scale.addEventListener('scroll', () => {
					clearTimeout(this.scrollSettleTimeout);
					this.scrollSettleTimeout = window.setTimeout(() => this.scheduleRecenter(), 120);
				});
			}

			onManualNav(direction) {
				if (!this.copyWidth) return;

				this.scale.scrollBy({ left: direction * this.scale.clientWidth * MANUAL_STEP_RATIO, behavior: 'smooth' });
				this.scheduleRecenter();
			}

			scheduleRecenter() {
				clearTimeout(this.recenterTimeout);
				this.recenterTimeout = window.setTimeout(() => this.recenter(), MANUAL_RECENTER_DELAY);
			}

			recenter() {
				if (!this.copyWidth || !this.copiesPerSide) return;

				const target = this.copiesPerSide * this.copyWidth; 
				const current = this.scale.scrollLeft;
				const diff = current - target;
				const buffer = this.copyWidth * (this.copiesPerSide - 0.5);

				if (Math.abs(diff) > buffer) {
					const wraps = Math.round(diff / this.copyWidth);
					this.scale.scrollLeft = current - wraps * this.copyWidth;
				}
			}
		}
	);
})();